// @ts-nocheck
import { memo, useCallback } from "react";

import { cx } from "@emotion/css";
import { Card, Fonts } from "@snapchat/snap-design-system";

import { ActionModal } from "../../../common/components/ActionModal";
import { itemsModalStyle, modalContainerStyle, modalStyle, modalWidth, newGroupWrapper } from "../../../common/styles";
import { useSelection } from "../../../core/hooks";
import { useInputState } from "../../../core/validation";
import { type NewGroup } from "../../../dashboard/models";
import { type GroupsGridItemModel } from "../../grid/model";

import { AddToLensGroupTable } from "./AddToLensGroupTable";
import { NewLensGroupInput } from "./NewLensGroupInput";

export interface LensInAddLensGroupInfo {
    priority: number;
}

export interface AddLensGroupInfo {
    id: string;
    name: string;
    totalNumLenses: number;
    lenses: readonly LensInAddLensGroupInfo[];
}

interface AddToLensGroupModalProps {
    visible: boolean;
    groupIsCreated: boolean;
    onCancel: () => void;
    onAddToGroup: (groupIds: AddLensGroupInfo[]) => void;
    onCreateGroup: (newGroup: NewGroup) => void;
    alreadySelectedGroupIds: string[];
    newGroups: ReadonlySet<string>;
}

export const AddToLensGroupModal = memo<AddToLensGroupModalProps>(function AddToLensGroupModal({
    visible,
    onCancel,
    onAddToGroup,
    onCreateGroup,
    groupIsCreated,
    alreadySelectedGroupIds,
    newGroups,
}) {
    const { selectedItems, resetSelection, toggleItemSelection } = useSelection<GroupsGridItemModel>(
        (gridItem) => gridItem?.id!,
    );
    const { reset: resetSearch } = useInputState<string>({
        error: "",
        validator: () => true,
        defaultValue: "",
    });

    const cancel = useCallback(() => {
        onCancel();
        resetSearch();
    }, [onCancel, resetSearch]);

    const addToGroup = useCallback(() => {
        const selectedGroupIds = Array.from(selectedItems?.values() ?? []).map((item) => ({
            id: item.id!,
            name: item.name!,
            totalNumLenses: item.totalNumLenses,
            lenses: item.lenses,
        }));
        onAddToGroup(selectedGroupIds);
        resetSelection();
        cancel();
    }, [cancel, onAddToGroup, resetSelection, selectedItems]);

    return (
        <>
            <ActionModal
                className={cx(modalStyle, itemsModalStyle)}
                padBody={false}
                actionText="Add to Selected"
                visible={visible}
                onCancel={cancel}
                onAction={addToGroup}
                didClose={resetSelection}
                isActionButtonDisabled={selectedItems.size <= 0}
                width={modalWidth}
                data-testid="add-to-lens-group-modal"
            >
                <Card
                    title="Add to Lens Group"
                    titleStyle={Fonts.Heading.H500}
                    padBody={false}
                    titleBorder={false}
                    className={modalContainerStyle}
                >
                    <div className={newGroupWrapper}>
                        <NewLensGroupInput onCreate={onCreateGroup} isCreated={groupIsCreated} />
                    </div>
                    <AddToLensGroupTable
                        newGroups={newGroups}
                        onToggleSelection={toggleItemSelection}
                        selectedGroups={selectedItems}
                        alreadySelectedIds={new Set(alreadySelectedGroupIds)}
                    />
                </Card>
            </ActionModal>
        </>
    );
});
